import React, { useState, useEffect } from 'react';
import { awardsAPI } from '../../utils/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import AwardForm from '../../components/admin/AwardForm';
import { FaEdit, FaTrash, FaPlus, FaTrophy } from 'react-icons/fa';

const AwardManager = () => {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAward, setCurrentAward] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAwards();
  }, []);

  const fetchAwards = async () => {
    try {
      setLoading(true);
      const response = await awardsAPI.getAll();
      const sortedAwards = (response.data.data || []).sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      setAwards(sortedAwards);
    } catch (err) {
      setError(err.message || 'Failed to load awards');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this award?')) {
      return;
    }

    try {
      await awardsAPI.delete(id);
      setAwards(awards.filter((a) => a._id !== id));
    } catch (err) {
      alert(err.message || 'Failed to delete award');
    }
  };

  const handleOpenModal = (award = null) => {
    setCurrentAward(award);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentAward(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      if (currentAward) {
        // Update existing award
        const response = await awardsAPI.update(currentAward._id, formData);
        const updatedAwards = awards.map((a) =>
          a._id === currentAward._id ? response.data.data : a
        );
        setAwards(updatedAwards.sort((a, b) => a.sortOrder - b.sortOrder));
      } else {
        // Create new award
        const response = await awardsAPI.create(formData);
        const newAwards = [response.data.data, ...awards];
        setAwards(newAwards.sort((a, b) => a.sortOrder - b.sortOrder));
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message || 'Failed to save award');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading awards..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
            Manage Awards
          </h1>
          <p className="text-text-primary font-body">
            Showcase your achievements and recognition
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <FaPlus className="inline mr-2" />
          New Award
        </Button>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} className="mb-6" />}

      {/* Awards List */}
      {awards.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-text-primary font-body">
            No awards yet. Add your first award!
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {awards.map((award) => (
            <Card key={award._id} className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-accent bg-opacity-20 rounded-full flex items-center justify-center">
                    <FaTrophy className="text-accent text-xl" />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-secondary">
                        {award.title}
                      </h3>
                      {award.year && (
                        <span className="text-sm font-body text-accent font-medium">
                          {award.year}
                        </span>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenModal(award)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(award._id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>

                  <p className="mt-2 text-text-primary font-body">
                    {award.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={currentAward ? 'Edit Award' : 'Create New Award'}
        size="large"
      >
        <AwardForm
          award={currentAward}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default AwardManager;
